import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "./button";
import { toast } from "react-toastify";
import { Card, CardContent } from "./card";

import SelectField from "./SelectField";
import InputField from "./InputField";
import TextAreaField from "./TextAreaField";
import SelectSimple from "./SelectSimple";

const TiposMantenimiento = ["Preventivo", "Correctivo", "Calibración"];

// ─── Utilidades de distribución ───────────────────────────────────────────────

/** Parsea "YYYY-MM-DD" como fecha local, evitando el desfase UTC de new Date("YYYY-MM-DD"). */
function parseDateLocal(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Retorna los días válidos entre dos fechas (inclusive), excluyendo fines de semana si aplica. */
function calcularDiasValidos(fechaInicio, fechaFin, omitirFinesSemana) {
  const dias = [];
  const current = new Date(parseDateLocal(fechaInicio));
  const fin = parseDateLocal(fechaFin);

  while (current <= fin) {
    const dow = current.getDay();
    if (!omitirFinesSemana || (dow !== 0 && dow !== 6)) {
      dias.push(new Date(current));
    }
    current.setDate(current.getDate() + 1);
  }
  return dias;
}

/**
 * Agrupa los equipos respetando la jerarquía grupo → área.
 * Retorna: { [grupoId]: { grupoNombre, grupoId, areas: { [areaId]: { areaNombre, equipos[] } } } }
 */
function agruparEquiposPorJerarquia(selectedEquipos, selectedAreas) {
  const areaMap = {};
  selectedAreas.forEach((area) => {
    areaMap[String(area._id)] = area;
  });

  const grupos = {};
  selectedEquipos.forEach((equipo) => {
    const areaId = String(equipo.area?._id ?? equipo.area);
    const area = areaMap[areaId];
    if (!area) return;

    const grupoId = String(area.grupo?._id ?? area.grupo);
    const grupoNombre = area.grupo?.name ?? grupoId;

    if (!grupos[grupoId]) {
      grupos[grupoId] = { grupoNombre, grupoId, areas: {} };
    }
    if (!grupos[grupoId].areas[areaId]) {
      grupos[grupoId].areas[areaId] = { areaNombre: area.name, equipos: [] };
    }
    grupos[grupoId].areas[areaId].equipos.push(equipo);
  });

  return grupos;
}

/**
 * Convierte el árbol grupo→área en una lista plana de bloques asignables.
 * Un bloque = combinación (grupo + área). Es la unidad mínima que se prefiere no dividir.
 */
function construirBloques(grupos) {
  const bloques = [];
  Object.values(grupos).forEach((grupo) => {
    Object.entries(grupo.areas).forEach(([areaId, area]) => {
      bloques.push({
        grupoNombre: grupo.grupoNombre,
        grupoId: grupo.grupoId,
        areaNombre: area.areaNombre,
        areaId,
        equipos: [...area.equipos],
      });
    });
  });
  return bloques;
}

/**
 * Distribuye bloques entre responsables de forma balanceada.
 *
 * Caso A — bloques >= responsables:
 *   Asignación greedy: el bloque más grande va al responsable con menor carga.
 *   Los bloques se mantienen íntegros (mismo grupo+área = mismo responsable).
 *
 * Caso B — bloques < responsables (p.ej. 1 área, 2 responsables):
 *   Se divide el total de equipos equitativamente (floor + sobrante 1 a 1),
 *   anotando la metadata de grupo/área para la previsualización.
 *
 * Retorna: [{ persona, bloques: [{ grupoNombre, areaNombre, equipos[] }] }]
 */
function asignarBloques(bloques, responsables) {
  const asignaciones = responsables.map((p) => ({ persona: p, bloques: [] }));
  const totalEquipos = (asig) =>
    asig.bloques.reduce((sum, b) => sum + b.equipos.length, 0);

  if (bloques.length >= responsables.length) {
    // Greedy: ordenar bloques de mayor a menor para minimizar desequilibrio
    const sorted = [...bloques].sort(
      (a, b) => b.equipos.length - a.equipos.length,
    );
    sorted.forEach((bloque) => {
      const target = asignaciones.reduce((min, curr) =>
        totalEquipos(curr) < totalEquipos(min) ? curr : min,
      );
      target.bloques.push({ ...bloque, equipos: [...bloque.equipos] });
    });
  } else {
    // División a nivel de equipo con metadata de bloque para previsualización
    const equiposAnotados = bloques.flatMap((b) =>
      b.equipos.map((e) => ({
        equipo: e,
        grupoNombre: b.grupoNombre,
        grupoId: b.grupoId,
        areaNombre: b.areaNombre,
        areaId: b.areaId,
      })),
    );

    const total = equiposAnotados.length;
    const base = Math.floor(total / responsables.length);
    const sobrantes = total % responsables.length;
    let idx = 0;

    asignaciones.forEach((asig, i) => {
      const cantidad = base + (i < sobrantes ? 1 : 0);
      const slice = equiposAnotados.slice(idx, idx + cantidad);
      idx += cantidad;

      // Reconstruir bloques parciales manteniendo la metadata grupo/área
      const bloquesMap = {};
      slice.forEach(({ equipo, grupoNombre, grupoId, areaNombre, areaId }) => {
        const key = `${grupoId}__${areaId}`;
        if (!bloquesMap[key]) {
          bloquesMap[key] = { grupoNombre, areaNombre, equipos: [] };
        }
        bloquesMap[key].equipos.push(equipo);
      });
      asig.bloques = Object.values(bloquesMap);
    });
  }

  return asignaciones;
}

/**
 * Genera el plan final de distribución: una entrada por (día × responsable).
 * Usa floor + sobrante para distribución exacta entre días (sin redondeo al alza).
 *
 * Retorna: [{ fechaStr, fechaDisplay, personaId, personaNombre, bloques[], equipoIds[] }]
 */
function generarPlanDistribucion(diasValidos, asignaciones, horaEjecucion) {
  const plan = [];
  const hora = horaEjecucion || "08:00";

  asignaciones.forEach(({ persona, bloques: bloquesResponsable }) => {
    // Lista plana anotada para luego reconstruir bloques por día
    const equiposAnotados = bloquesResponsable.flatMap((b) =>
      b.equipos.map((e) => ({
        equipo: e,
        grupoNombre: b.grupoNombre,
        areaNombre: b.areaNombre,
      })),
    );

    if (!equiposAnotados.length) return;

    const total = equiposAnotados.length;
    const totalDias = diasValidos.length;
    const base = Math.floor(total / totalDias);
    const sobrantes = total % totalDias;
    let idx = 0;

    diasValidos.forEach((dia, diaIdx) => {
      const cantidad = base + (diaIdx < sobrantes ? 1 : 0);
      if (cantidad === 0) return;

      const sliceAnotado = equiposAnotados.slice(idx, idx + cantidad);
      idx += cantidad;

      // Reconstruir bloques del slice para la previsualización
      const bloquesMap = {};
      sliceAnotado.forEach(({ equipo, grupoNombre, areaNombre }) => {
        const key = `${grupoNombre}__${areaNombre}`;
        if (!bloquesMap[key]) {
          bloquesMap[key] = { grupoNombre, areaNombre, equipos: [] };
        }
        bloquesMap[key].equipos.push(equipo);
      });

      const yyyy = dia.getFullYear();
      const mm = String(dia.getMonth() + 1).padStart(2, "0");
      const dd = String(dia.getDate()).padStart(2, "0");

      plan.push({
        fechaStr: `${yyyy}-${mm}-${dd}T${hora}`,
        fechaDisplay: new Date(dia),
        personaId: persona._id,
        personaNombre: persona.name,
        bloques: Object.values(bloquesMap),
        equipoIds: sliceAnotado.map(({ equipo }) => equipo._id ?? equipo),
      });
    });
  });

  return plan;
}

// ─── Sub-componente: Previsualización interactiva ─────────────────────────────

function PlanPreviewPanel({ plan, estadisticas }) {
  const [diasAbiertos, setDiasAbiertos] = useState({});
  const [responsablesAbiertos, setResponsablesAbiertos] = useState({});

  // Agrupar entradas del plan por día para el acordeón
  const diasAgrupados = useMemo(() => {
    const map = {};
    plan.forEach((entrada) => {
      const key = entrada.fechaStr.slice(0, 10);
      if (!map[key]) {
        map[key] = {
          key,
          fechaDisplay: entrada.fechaDisplay,
          responsables: [],
        };
      }
      map[key].responsables.push(entrada);
    });
    return Object.values(map);
  }, [plan]);

  const toggleDia = (key) =>
    setDiasAbiertos((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleResponsable = (key) =>
    setResponsablesAbiertos((prev) => ({ ...prev, [key]: !prev[key] }));

  const formatFechaLarga = (date) =>
    date.toLocaleDateString("es-CO", {
      weekday: "long",
      day: "2-digit",
      month: "short",
    });

  return (
    <div className="border border-green-200 bg-green-50/30 rounded-xl p-4 space-y-3">
      {/* Encabezado */}
      <div className="flex flex-wrap justify-between items-center gap-2">
        <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">
          Vista previa de la distribución
        </p>
        <span className="text-xs text-gray-500">
          {diasAgrupados.length} día(s) · {plan.length} lote(s) a programar
        </span>
      </div>

      {/* Resumen numérico */}
      {estadisticas && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { label: "Equipos", value: estadisticas.totalEquipos },
            { label: "Días efectivos", value: estadisticas.totalDias },
            { label: "Máx. por día", value: estadisticas.equiposPorDiaMax },
            {
              label: "Máx. resp/día",
              value: estadisticas.equiposPorResponsablePorDiaMax,
            },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="bg-white border border-green-100 rounded-lg p-2 text-center"
            >
              <p className="text-base font-bold text-green-700">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          ))}
        </div>
      )}

      {estadisticas?.diasExcluidos > 0 && (
        <p className="text-xs text-amber-600">
          * {estadisticas.diasExcluidos} día(s) excluido(s) por ser fin de
          semana
        </p>
      )}

      {/* Acordeón por día */}
      <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
        {diasAgrupados.map((dia) => {
          const diaAbierto = diasAbiertos[dia.key] ?? false;
          const totalDia = dia.responsables.reduce(
            (sum, r) => sum + r.equipoIds.length,
            0,
          );

          return (
            <div
              key={dia.key}
              className="border border-green-200 rounded-lg overflow-hidden bg-white"
            >
              {/* Cabecera del día */}
              <button
                type="button"
                onClick={() => toggleDia(dia.key)}
                className="w-full flex justify-between items-center px-3 py-2 text-left hover:bg-green-50 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="text-green-500 text-xs font-bold shrink-0"
                    aria-hidden
                  >
                    {diaAbierto ? "▼" : "▶"}
                  </span>
                  <span className="text-sm font-semibold text-gray-800 capitalize truncate">
                    {formatFechaLarga(dia.fechaDisplay)}
                  </span>
                </div>
                <span className="text-xs font-semibold text-green-700 shrink-0 ml-2 bg-green-100 px-2 py-0.5 rounded-full">
                  {totalDia} eq.
                </span>
              </button>

              {/* Responsables del día */}
              {diaAbierto && (
                <div className="border-t border-green-100 divide-y divide-gray-100">
                  {dia.responsables.map((entrada) => {
                    const respKey = `${dia.key}__${entrada.personaId ?? "none"}`;
                    const respAbierto = responsablesAbiertos[respKey] ?? false;

                    return (
                      <div key={respKey}>
                        {/* Cabecera responsable */}
                        <button
                          type="button"
                          onClick={() => toggleResponsable(respKey)}
                          className="w-full flex justify-between items-center px-5 py-2 text-left hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className="text-gray-400 text-xs shrink-0"
                              aria-hidden
                            >
                              {respAbierto ? "▼" : "▶"}
                            </span>
                            <span className="text-xs font-medium text-gray-700 truncate">
                              {entrada.personaNombre}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 shrink-0 ml-2">
                            {entrada.equipoIds.length} equipo(s)
                          </span>
                        </button>

                        {/* Bloques del responsable */}
                        {respAbierto && (
                          <div className="px-7 pb-3 pt-1 space-y-2 bg-gray-50/60">
                            {entrada.bloques.map((bloque, bi) => (
                              <div key={bi}>
                                <p className="text-xs font-semibold text-gray-600 mb-1">
                                  <span className="text-blue-600">
                                    {bloque.grupoNombre}
                                  </span>
                                  <span className="text-gray-400 mx-1">/</span>
                                  <span>{bloque.areaNombre}</span>
                                  <span className="text-gray-400 ml-1 font-normal">
                                    ({bloque.equipos.length})
                                  </span>
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {bloque.equipos.map((eq) => (
                                    <span
                                      key={eq._id ?? String(eq)}
                                      className="inline-block bg-white border border-gray-200 rounded px-1.5 py-0.5 text-xs text-gray-600 font-mono"
                                    >
                                      {eq.codigo ?? eq._id ?? String(eq)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

const CronogramaMasivoForm = ({ onClose, onSave }) => {
  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState("");

  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState("");

  const [areas, setAreas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);

  const [equipos, setEquipos] = useState([]);
  const [selectedEquipos, setSelectedEquipos] = useState([]);

  const [personas, setPersonas] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(""); // modo simple
  const [selectedPersonas, setSelectedPersonas] = useState([]); // modo rango

  const [fechaProgramada, setFechaProgramada] = useState("");
  const [tipo, setTipo] = useState(TiposMantenimiento[0]);
  const [observaciones, setObservaciones] = useState("");
  const [loading, setLoading] = useState(false);

  // ── Estados para distribución por rango ──
  const [modoRango, setModoRango] = useState(false);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [horaEjecucion, setHoraEjecucion] = useState("08:00");
  const [omitirFinesSemana, setOmitirFinesSemana] = useState(false);
  const [planDistribucion, setPlanDistribucion] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;

  // ── Selección rápida ──
  const seleccionarTodosEquipos = () => setSelectedEquipos(equipos);
  const limpiarEquipos = () => setSelectedEquipos([]);
  const seleccionarTodasAreas = () => setSelectedAreas(areas);
  const limpiarAreas = () => setSelectedAreas([]);

  // Invalidar plan cuando cambian los inputs que lo afectan
  useEffect(() => {
    setPlanDistribucion(null);
    setShowPreview(false);
  }, [
    selectedEquipos,
    selectedPersonas,
    fechaInicio,
    fechaFin,
    omitirFinesSemana,
    horaEjecucion,
  ]);

  // ─── Carga de datos ──────────────────────────────────────────────────────

  useEffect(() => {
    axios
      .get(`${API}/clientes`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setClientes(res.data))
      .catch(() => toast.error("Error al cargar clientes"));
  }, [API, token]);

  useEffect(() => {
    if (!selectedCliente) {
      setSedes([]);
      setSelectedSede("");
      setAreas([]);
      setSelectedAreas([]);
      setEquipos([]);
      setSelectedEquipos([]);
      return;
    }
    setSelectedSede("");
    setSelectedAreas([]);
    setEquipos([]);
    setSelectedEquipos([]);
    axios
      .get(`${API}/sedes/${selectedCliente}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setSedes(res.data || []))
      .catch(() => toast.error("Error al cargar sedes"));
  }, [selectedCliente, API, token]);

  useEffect(() => {
    if (!selectedCliente) {
      setAreas([]);
      setSelectedAreas([]);
      setEquipos([]);
      setSelectedEquipos([]);
      return;
    }
    axios
      .get(
        `${API}/areas/con_equipos?cliente=${selectedCliente}&sede=${selectedSede || "all"}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => setAreas(res.data))
      .catch(() => toast.error("Error al cargar áreas"));
  }, [selectedCliente, selectedSede, API, token]);

  useEffect(() => {
    setSelectedAreas([]);
    setEquipos([]);
    setSelectedEquipos([]);
  }, [selectedSede]);

  useEffect(() => {
    const areaIds = selectedAreas
      .map((area) => area?._id ?? area)
      .filter(Boolean);
    if (!areaIds.length) {
      setEquipos([]);
      setSelectedEquipos([]);
      return;
    }
    const sedeQuery = selectedSede ? `&sede=${selectedSede}` : "";
    axios
      .get(`${API}/equipos?area=${areaIds.join(",")}${sedeQuery}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setEquipos(res.data))
      .catch(() => toast.error("Error al cargar equipos"));
  }, [selectedAreas, selectedSede, API, token]);

  useEffect(() => {
    axios
      .get(`${API}/users/resp`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setPersonas(res.data))
      .catch(() => toast.error("Error al cargar personas"));
  }, [API, token]);

  // ─── Estadísticas rápidas en tiempo real ────────────────────────────────

  const estadisticasDistribucion = useMemo(() => {
    if (!modoRango || !fechaInicio || !fechaFin || !selectedEquipos.length)
      return null;

    const diasValidos = calcularDiasValidos(
      fechaInicio,
      fechaFin,
      omitirFinesSemana,
    );
    const totalDiasSinFiltro = calcularDiasValidos(
      fechaInicio,
      fechaFin,
      false,
    ).length;
    if (!diasValidos.length) return null;

    const numResponsables = selectedPersonas.length || 1;
    const totalEquipos = selectedEquipos.length;

    // Usar floor + 1 para reflejar la distribución exacta (misma lógica que generarPlanDistribucion)
    const basePorDia = Math.floor(totalEquipos / diasValidos.length);
    const equiposPorDiaMax =
      basePorDia + (totalEquipos % diasValidos.length > 0 ? 1 : 0);

    const basePorResp = Math.floor(totalEquipos / numResponsables);
    const equiposPorResp =
      basePorResp + (totalEquipos % numResponsables > 0 ? 1 : 0);

    const baseRespDia = Math.floor(equiposPorResp / diasValidos.length);
    const equiposPorResponsablePorDiaMax =
      baseRespDia + (equiposPorResp % diasValidos.length > 0 ? 1 : 0);

    return {
      totalEquipos,
      totalDias: diasValidos.length,
      diasExcluidos: totalDiasSinFiltro - diasValidos.length,
      equiposPorDiaMax,
      numResponsables,
      equiposPorResponsablePorDiaMax,
    };
  }, [
    modoRango,
    fechaInicio,
    fechaFin,
    omitirFinesSemana,
    selectedEquipos,
    selectedPersonas,
  ]);

  // ─── Generar plan ────────────────────────────────────────────────────────

  const handleGenerarPlan = () => {
    if (!fechaInicio || !fechaFin)
      return toast.warning("Seleccione el rango de fechas");
    if (parseDateLocal(fechaFin) < parseDateLocal(fechaInicio))
      return toast.warning("La fecha fin debe ser posterior a la fecha inicio");
    if (!selectedEquipos.length)
      return toast.warning("Seleccione al menos un equipo");

    const diasValidos = calcularDiasValidos(
      fechaInicio,
      fechaFin,
      omitirFinesSemana,
    );
    if (!diasValidos.length)
      return toast.warning("No hay días válidos en el rango seleccionado");

    const responsables = selectedPersonas.length
      ? selectedPersonas
      : [{ _id: null, name: "Sin asignar" }];

    const grupos = agruparEquiposPorJerarquia(selectedEquipos, selectedAreas);
    const bloques = construirBloques(grupos);
    const asignaciones = asignarBloques(bloques, responsables);
    const plan = generarPlanDistribucion(
      diasValidos,
      asignaciones,
      horaEjecucion,
    );

    setPlanDistribucion(plan);
    setShowPreview(true);
  };

  // ─── Validación ──────────────────────────────────────────────────────────

  const validate = () => {
    if (!selectedCliente) return toast.warning("Seleccione un cliente");
    if (!selectedAreas.length)
      return toast.warning("Seleccione al menos un área");
    const equiposIds = selectedEquipos.map((e) => e?._id ?? e).filter(Boolean);
    if (!equiposIds.length)
      return toast.warning("Seleccione al menos un equipo");
    if (!tipo) return toast.warning("Seleccione el tipo de mantenimiento");

    if (modoRango) {
      if (!fechaInicio) return toast.warning("Ingrese la fecha de inicio");
      if (!fechaFin) return toast.warning("Ingrese la fecha de fin");
      if (parseDateLocal(fechaFin) < parseDateLocal(fechaInicio))
        return toast.warning(
          "La fecha fin debe ser posterior a la fecha inicio",
        );
      if (!planDistribucion)
        return toast.warning("Genere la distribución antes de guardar");
    } else {
      if (!fechaProgramada) return toast.warning("Ingrese la fecha programada");
    }

    return true;
  };

  // ─── Envío modo simple (flujo original sin cambios) ──────────────────────

  const handleSubmitSimple = async () => {
    const equiposSeleccionados = selectedEquipos
      .map((equipo) => equipo?._id ?? equipo)
      .filter(Boolean);
    const areasSeleccionadas = selectedAreas
      .map((area) => area?._id ?? area)
      .filter(Boolean);

    const payload = {
      clienteId: selectedCliente,
      areaIds: areasSeleccionadas,
      equipos: equiposSeleccionados,
      personaId: selectedPersona || null,
      fechaProgramada,
      tipo,
      observaciones,
    };

    await axios.post(`${API}/cronograma/multiple`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  // ─── Envío modo rango (una llamada por entrada del plan) ─────────────────

  const handleSubmitRango = async () => {
    const areasSeleccionadas = selectedAreas
      .map((area) => area?._id ?? area)
      .filter(Boolean);

    for (const entrada of planDistribucion) {
      await axios.post(
        `${API}/cronograma/multiple`,
        {
          clienteId: selectedCliente,
          areaIds: areasSeleccionadas,
          equipos: entrada.equipoIds,
          personaId: entrada.personaId || null,
          fechaProgramada: entrada.fechaStr,
          tipo,
          observaciones,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    }
  };

  // ─── Submit principal ─────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      if (modoRango) {
        await handleSubmitRango();
      } else {
        await handleSubmitSimple();
      }
      toast.success("Mantenimientos programados correctamente");
      onSave();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Error al guardar mantenimientos masivos",
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <Card className="border-none shadow-none">
          <CardContent className="p-6 space-y-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold text-gray-800">
                Programación masiva de mantenimientos
              </h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>

            {/* ── Campos base (siempre visibles) ── */}
            <SelectField
              label="Cliente *"
              name="cliente"
              value={selectedCliente}
              options={clientes}
              valueKey="_id"
              displayFn={(c) => c.name}
              placeholder="Seleccione cliente"
              onChange={(e) => setSelectedCliente(e.target.value)}
            />

            <SelectField
              label="Sede"
              name="sede"
              value={selectedSede}
              options={sedes}
              valueKey="_id"
              displayFn={(s) => s.name}
              placeholder={
                selectedCliente ? "Todas las sedes" : "Seleccione un cliente"
              }
              onChange={(e) => setSelectedSede(e.target.value)}
            />

            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium">Área *</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={seleccionarTodasAreas}
                    disabled={!areas.length}
                    className="text-xs text-blue-600 hover:underline disabled:text-gray-400"
                  >
                    Seleccionar todas
                  </button>
                  <button
                    type="button"
                    onClick={limpiarAreas}
                    disabled={!selectedAreas.length}
                    className="text-xs text-red-500 hover:underline disabled:text-gray-400"
                  >
                    Limpiar
                  </button>
                </div>
              </div>
              <SelectField
                name="area"
                value={
                  Array.isArray(selectedAreas)
                    ? selectedAreas.map((area) => area._id)
                    : []
                }
                options={areas}
                valueKey="_id"
                displayFn={(a) => `${a.grupo.name} - ${a.name}`}
                placeholder="Seleccione una o varias áreas"
                isMulti
                onChange={(e) => setSelectedAreas(e.target.value)}
              />
              {areas.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedAreas.length} de {areas.length} áreas seleccionadas
                </p>
              )}
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium">Equipos *</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={seleccionarTodosEquipos}
                    disabled={!equipos.length}
                    className="text-xs text-blue-600 hover:underline disabled:text-gray-400"
                  >
                    Seleccionar todos
                  </button>
                  <button
                    type="button"
                    onClick={limpiarEquipos}
                    disabled={!selectedEquipos.length}
                    className="text-xs text-red-500 hover:underline disabled:text-gray-400"
                  >
                    Limpiar
                  </button>
                </div>
              </div>
              <SelectField
                name="equipos"
                value={
                  Array.isArray(selectedEquipos)
                    ? selectedEquipos.map((e) => e._id)
                    : []
                }
                options={equipos}
                valueKey="_id"
                displayFn={(e) =>
                  `${e.marca?.name} ${e.modelo?.name} [${e.codigo}]`
                }
                placeholder="Seleccione equipos"
                isMulti
                onChange={(e) => setSelectedEquipos(e.target.value)}
              />
              {equipos.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedEquipos.length} de {equipos.length} equipos
                  seleccionados
                </p>
              )}
            </div>

            <SelectSimple
              label="Tipo de mantenimiento *"
              name="tipo"
              value={tipo}
              options={TiposMantenimiento}
              onChange={(e) => setTipo(e.target.value)}
            />

            {/* ── Toggle: activar distribución por rango ── */}
            <div className="flex items-center gap-2 pt-1">
              <input
                id="modoRango"
                type="checkbox"
                checked={modoRango}
                onChange={(e) => {
                  setModoRango(e.target.checked);
                  setPlanDistribucion(null);
                  setShowPreview(false);
                }}
                className="w-4 h-4 cursor-pointer accent-blue-600"
              />
              <label
                htmlFor="modoRango"
                className="text-sm font-medium cursor-pointer select-none"
              >
                Distribuir por rango de fechas
              </label>
            </div>

            {/* ── Modo simple: responsable + fecha única (flujo original) ── */}
            {!modoRango && (
              <>
                <SelectField
                  label="Responsable"
                  name="persona"
                  value={selectedPersona}
                  options={personas}
                  valueKey="_id"
                  displayFn={(p) => p.name}
                  placeholder="Seleccione persona"
                  onChange={(e) => setSelectedPersona(e.target.value)}
                />
                <InputField
                  label="Fecha programada *"
                  type="datetime-local"
                  value={fechaProgramada}
                  onChange={(e) => setFechaProgramada(e.target.value)}
                />
              </>
            )}

            {/* ── Modo rango: sección de distribución inteligente ── */}
            {modoRango && (
              <div className="border border-blue-200 bg-blue-50/40 rounded-xl p-4 space-y-3 w-full min-w-0">
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                  Distribución por rango de fechas
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InputField
                    label="Fecha inicio *"
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                  />
                  <InputField
                    label="Fecha fin *"
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                  />
                </div>

                <InputField
                  label="Hora de ejecución"
                  type="time"
                  value={horaEjecucion}
                  onChange={(e) => setHoraEjecucion(e.target.value)}
                />

                <div className="flex items-center gap-2">
                  <input
                    id="omitirFds"
                    type="checkbox"
                    checked={omitirFinesSemana}
                    onChange={(e) => setOmitirFinesSemana(e.target.checked)}
                    className="w-4 h-4 cursor-pointer accent-blue-600"
                  />
                  <label
                    htmlFor="omitirFds"
                    className="text-sm cursor-pointer select-none"
                  >
                    Omitir sábados y domingos
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Responsables
                  </label>
                  <SelectField
                    name="personas"
                    value={
                      Array.isArray(selectedPersonas)
                        ? selectedPersonas.map((p) => p._id)
                        : []
                    }
                    options={personas}
                    valueKey="_id"
                    displayFn={(p) => p.name}
                    placeholder="Seleccione uno o varios responsables"
                    isMulti
                    onChange={(e) => setSelectedPersonas(e.target.value)}
                  />
                  {selectedPersonas.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedPersonas.length} responsable(s) seleccionado(s)
                    </p>
                  )}
                </div>

                {/* Estadísticas en tiempo real */}
                {estadisticasDistribucion && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                    {[
                      {
                        label: "Equipos totales",
                        value: estadisticasDistribucion.totalEquipos,
                      },
                      {
                        label: "Días efectivos",
                        value: estadisticasDistribucion.totalDias,
                      },
                      {
                        label: "Máx. por día",
                        value: estadisticasDistribucion.equiposPorDiaMax,
                      },
                      {
                        label: "Máx. resp/día",
                        value:
                          estadisticasDistribucion.equiposPorResponsablePorDiaMax,
                      },
                    ].map(({ label, value }) => (
                      <div
                        key={label}
                        className="bg-white border border-blue-100 rounded-lg p-2 text-center"
                      >
                        <p className="text-lg font-bold text-blue-700">
                          {value}
                        </p>
                        <p className="text-xs text-gray-500">{label}</p>
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerarPlan}
                  disabled={
                    !fechaInicio || !fechaFin || !selectedEquipos.length
                  }
                  className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  Previsualizar distribución
                </Button>
              </div>
            )}

            {/* ── Panel de previsualización interactivo ── */}
            {showPreview && planDistribucion && (
              <PlanPreviewPanel
                plan={planDistribucion}
                estadisticas={estadisticasDistribucion}
              />
            )}

            <TextAreaField
              label="Observaciones"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  loading || !equipos.length || (modoRango && !planDistribucion)
                }
              >
                {loading ? "Guardando..." : "Programar masivamente"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CronogramaMasivoForm;
